// Fetch all unique languages from API.Bible bibles
export async function fetchApiBibleLanguages() {
  const cacheKey = 'apiBibleApi_languages';
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    try {
      const { data, expiry } = JSON.parse(cached);
      if (!expiry || Date.now() < expiry) {
        return data;
      }
    } catch {}
  }
  const res = await fetch(`${API_BIBLE_BASE}/bibles`, {
    headers: {
      'api-key': API_BIBLE_KEY
    }
  });
  if (!res.ok) throw new Error('API.Bible error');
  const data = await res.json();
  // Extract and deduplicate languages
  const languageMap = new Map();
  data.data.forEach(bible => {
    const lang = bible.language;
    if (lang && lang.id) {
      languageMap.set(lang.id, {
        id: lang.id,
        name: lang.name,
        script: lang.script,
        direction: lang.direction
      });
    }
  });
  const languages = Array.from(languageMap.values());
  localStorage.setItem(cacheKey, JSON.stringify({ data: languages, expiry: Date.now() + 24 * 60 * 60 * 1000 }));
  return languages;
}
// src/services/apiBibleApi.js
// Service for fetching translations from API.Bible

// Use correct API.Bible endpoint
const API_BIBLE_BASE = 'https://rest.api.bible/v1';
const API_BIBLE_KEY = 'MEUeOm0UFqTYOUvW2bqP6';

// Fetch all available translations (bibles) from API.Bible
export async function fetchApiBibleTranslations() {
  const cacheKey = 'apiBibleApi_translations';
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    try {
      const { data, expiry } = JSON.parse(cached);
      if (!expiry || Date.now() < expiry) {
        return data;
      }
    } catch {}
  }
  const res = await fetch(`${API_BIBLE_BASE}/bibles`, {
    headers: {
      'api-key': API_BIBLE_KEY
    }
  });
  if (!res.ok) throw new Error('API.Bible error');
  const data = await res.json();
  const mapped = data.data.map(b => ({
    id: b.id,
    code: b.abbreviation,
    label: b.name,
    language: b.language.name,
    source: 'API.Bible'
  }));
  localStorage.setItem(cacheKey, JSON.stringify({ data: mapped, expiry: Date.now() + 24 * 60 * 60 * 1000 }));
  return mapped;
}

// Fetch all English translations (bibles) from API.Bible
export async function fetchAllEnglishApiBibleTranslations() {
  const cacheKey = 'apiBibleApi_english_translations';
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    try {
      const { data, expiry } = JSON.parse(cached);
      if (!expiry || Date.now() < expiry) {
        return data;
      }
    } catch {}
  }
  const res = await fetch(`${API_BIBLE_BASE}/bibles`, {
    headers: {
      'api-key': API_BIBLE_KEY
    }
  });
  if (!res.ok) throw new Error('API.Bible error');
  const data = await res.json();
  const mapped = data.data
    .filter(b => b.language.id === 'eng')
    .map(b => ({
      id: b.id,
      code: b.abbreviation,
      label: b.name,
      language: b.language.name,
      source: 'API.Bible'
    }));
  localStorage.setItem(cacheKey, JSON.stringify({ data: mapped, expiry: Date.now() + 24 * 60 * 60 * 1000 }));
  return mapped;
}

// Fetch all available English translations from API.Bible
export async function fetchAvailableEnglishApiBibleTranslations() {
  const cacheKey = 'apiBibleApi_available_english_translations';
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    try {
      const { data, expiry } = JSON.parse(cached);
      if (!expiry || Date.now() < expiry) {
        return data;
      }
    } catch {}
  }
  const res = await fetch(`${API_BIBLE_BASE}/bibles`, {
    headers: {
      'api-key': API_BIBLE_KEY
    }
  });
  if (!res.ok) throw new Error('API.Bible error');
  const data = await res.json();
  const mapped = data.data
    .filter(b => b.language.id === 'eng')
    .map(b => ({
      id: b.id,
      code: b.abbreviation,
      label: b.name,
      language: b.language.name,
      source: 'API.Bible'
    }));
  localStorage.setItem(cacheKey, JSON.stringify({ data: mapped, expiry: Date.now() + 24 * 60 * 60 * 1000 }));
  return mapped;
}

// Fetch a passage from API.Bible
// bookId: e.g. 'GEN', chapter: number, bibleId: from translations list
export async function fetchApiBiblePassage({ bibleId, bookId, chapter }) {
  const cacheKey = `apiBibleApi_passage_${bibleId}_${bookId}_${chapter}`;
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    try {
      const { data, expiry } = JSON.parse(cached);
      if (!expiry || Date.now() < expiry) {
        return data;
      }
    } catch {}
  }
  // API.Bible expects chapter as a string, e.g. 'GEN.1'
  const passageId = `${bookId}.${chapter}`;
  const url = `${API_BIBLE_BASE}/bibles/${bibleId}/chapters/${passageId}`;
  const res = await fetch(url, {
    headers: {
      'api-key': API_BIBLE_KEY
    }
  });
  if (!res.ok) throw new Error('API.Bible error');
  const data = await res.json();
  // Parse HTML content to extract verses robustly, including verse ranges
  const html = data.data.content;
  const cleanHtml = html.replace(/\n/g, ' ');
  // Find all verse number spans and their positions (handle ranges like 1-2, 3-5)
  const spanRegex = /<span[^>]*data-number="([\d-]+)"[^>]*>([\d-]+)<\/span>/g;
  let match;
  const indices = [];
  while ((match = spanRegex.exec(cleanHtml)) !== null) {
    indices.push({
      range: match[1],
      start: match.index,
      end: spanRegex.lastIndex
    });
  }
  const verses = [];
  for (let i = 0; i < indices.length; i++) {
    const { range, end } = indices[i];
    const nextStart = i + 1 < indices.length ? indices[i + 1].start : cleanHtml.length;
    let text = cleanHtml.slice(end, nextStart);
    text = text.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
    // If range is a single number, just add one verse
    if (/^\d+$/.test(range)) {
      verses.push({ verse: parseInt(range, 10), text });
    } else if (/^(\d+)-(\d+)$/.test(range)) {
      // If range is like 1-2, 3-5, add for each verse in the range
      const [startV, endV] = range.split('-').map(Number);
      for (let v = startV; v <= endV; v++) {
        verses.push({ verse: v, text });
      }
    }
  }
  // Sort by verse number to ensure order
  verses.sort((a, b) => a.verse - b.verse);
  localStorage.setItem(cacheKey, JSON.stringify({ data: verses, expiry: Date.now() + 24 * 60 * 60 * 1000 }));
  return verses;
}
